/**
 * Meeting-Cockpit v1 (CompanyBrain Welle 3, BEN-107).
 * Hooks fuer die Liste real indexierter Meetings + grounded KI-Analyse (Zusammenfassung,
 * Action-Items, Entscheidungen) + Uebernahme eines Action-Items in den Decision-Log.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "./api";
import { useTenant } from "./tenant";

export type Meeting = {
  id: string;
  file: string;
  title: string;
  kind: string;
  source: string;
  document_id: string;
  path: string;
  chunk_count: number;
  date: string;
  ingested_at: string;
};

export type MeetingsResponse = {
  tenant_id: string;
  lakehouse_tenant: string;
  count: number;
  meetings: Meeting[];
  degraded: boolean;
  index: string;
  generated_at: string;
};

export type ActionItem = { text: string; owner?: string };
export type MeetingDecision = { text: string };

export type MeetingAnalysis = {
  tenant_id: string;
  file: string;
  id: string;
  title: string;
  kind: string;
  source: string;
  document_ids: string[];
  chunks_used: number;
  grounded: boolean;
  openproject_write_enabled: boolean;
  summary: string;
  action_items: ActionItem[];
  decisions: MeetingDecision[];
  engine: string;
};

export function useMeetings() {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  return useQuery({
    queryKey: ["meetings", effectiveTenantId],
    queryFn: () => api.get<MeetingsResponse>("/meetings?limit=80"),
    enabled: Boolean(effectiveTenantId),
    retry: 1,
    staleTime: 60_000,
  });
}

export function useAnalyzeMeeting() {
  const api = useApi();
  return useMutation({
    mutationFn: (file: string) => api.post<MeetingAnalysis>("/meetings/analyze", { file }),
  });
}

export type ToDecisionResult = { ok: boolean; decision: { id: string }; route: string };

export function useMeetingToDecision() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { file: string; text: string; title?: string; options?: string }) =>
      api.post<ToDecisionResult>("/meetings/action-to-decision", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["decisions"] });
      qc.invalidateQueries({ queryKey: ["decisions-stats"] });
      qc.invalidateQueries({ queryKey: ["next-actions"] });
    },
  });
}
